/* global google, L */

const MAP_STYLES = [
  { stylers: [{ saturation: -100 }] },
  { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
]

const COUNTRY_ZOOM = 13
const STORE_ZOOM = 15
const BOUNDS_PADDING = 64
const LEAFLET_CSS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css'
const LEAFLET_JS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js'

class StoreLocations extends window.BAO.CustomElement() {
  connectedCallback () {
    this.activeStoreItemEl = null
    this.activeCountryKey = null
    this.map = null
    this.mapReady = false
    this.markers = []
    this.markerIcon = null
    this.markerBadgeHtml = null
    this.currentBounds = null
    this.devMode = this.dataset.mapsDevMode === 'true'

    super.connectedCallback()
  }

  setupListeners () {
    super.setupListeners()

    this.onCountryTriggerClick = this.onCountryTriggerClick.bind(this)
    this.onStoreTriggerClick = this.onStoreTriggerClick.bind(this)

    if (this.els?.['country-trigger']?.exists) {
      this.els['country-trigger'].elements.forEach((triggerEl) => {
        this.listeners.add(triggerEl, 'click', this.onCountryTriggerClick)
      })
    }

    if (this.els?.['store-trigger']?.exists) {
      this.els['store-trigger'].elements.forEach((triggerEl) => {
        this.listeners.add(triggerEl, 'click', this.onStoreTriggerClick)
      })
    }

    this.activateInitialCountry()
    this.initMap()
  }

  activateInitialCountry () {
    if (!this.els?.['country-trigger']?.exists) return

    const activeTriggerEl = this.els['country-trigger'].elements.find(
      (triggerEl) => triggerEl.getAttribute('aria-pressed') === 'true',
    )
    const countryKey =
      activeTriggerEl?.dataset?.countryKey ||
      this.els['country-trigger'].elements[0]?.dataset?.countryKey

    if (!countryKey) return

    this.activateCountry(countryKey)
  }

  onCountryTriggerClick (event) {
    const triggerEl = event.currentTarget
    const countryKey = triggerEl?.dataset?.countryKey

    if (!countryKey) return

    this.collapseActiveStore()
    this.activateCountry(countryKey)
  }

  activateCountry (countryKey) {
    this.els['country-trigger'].elements.forEach((triggerEl) => {
      const isActive = triggerEl.dataset.countryKey === countryKey

      triggerEl.classList.toggle(
        'sec-StoreLocations_CountryTrigger-active',
        isActive,
      )
      triggerEl.setAttribute('aria-pressed', String(isActive))
    })

    if (this.els.stores?.element) {
      this.els.stores.element.classList.add(
        'sec-StoreLocations_Stores-visible',
      )
      this.els.stores.element.setAttribute('aria-hidden', 'false')
    }

    if (this.els['stores-group']?.exists) {
      this.els['stores-group'].elements.forEach((groupEl) => {
        const isActive = groupEl.dataset.countryGroup === countryKey

        groupEl.classList.toggle(
          'sec-StoreLocations_StoresGroup-active',
          isActive,
        )
        groupEl.setAttribute('aria-hidden', String(!isActive))
      })
    }

    this.activeCountryKey = countryKey
    this.showCountryOnMap(countryKey)
  }

  onStoreTriggerClick (event) {
    const triggerEl = event.currentTarget
    const storeItemEl = triggerEl.closest(
      '[data-store-locations-el="store-item"]',
    )

    if (!storeItemEl) return

    const isActive = storeItemEl === this.activeStoreItemEl

    this.collapseActiveStore()

    if (isActive) {
      this.fitActiveCountry()
      return
    }

    this.expandStore(storeItemEl)
  }

  expandStore (storeItemEl, options = {}) {
    const triggerEl = storeItemEl.querySelector(
      '[data-store-locations-el="store-trigger"]',
    )
    const detailsEl = storeItemEl.querySelector(
      '[data-store-locations-el="store-details"]',
    )

    storeItemEl.classList.add('sec-StoreLocations_StoreItem-active')
    triggerEl?.setAttribute('aria-expanded', 'true')

    if (detailsEl) {
      detailsEl.hidden = false
    }

    this.activeStoreItemEl = storeItemEl

    this.focusStoreOnMap(storeItemEl)

    if (options.scrollIntoView) {
      storeItemEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }

  collapseActiveStore () {
    if (!this.activeStoreItemEl) return

    this.collapseStore(this.activeStoreItemEl)
    this.activeStoreItemEl = null
  }

  collapseStore (storeItemEl) {
    const triggerEl = storeItemEl.querySelector(
      '[data-store-locations-el="store-trigger"]',
    )
    const detailsEl = storeItemEl.querySelector(
      '[data-store-locations-el="store-details"]',
    )

    storeItemEl.classList.remove('sec-StoreLocations_StoreItem-active')
    triggerEl?.setAttribute('aria-expanded', 'false')

    if (detailsEl) {
      detailsEl.hidden = true
    }
  }

  async initMap () {
    const mapEl = this.els?.map?.element
    if (!mapEl) return

    // Re-read in case setupListeners ran before instance fields were set
    this.devMode = this.dataset.mapsDevMode === 'true'

    try {
      if (this.devMode) {
        await this.initLeafletMap(mapEl)
      } else {
        await this.initGoogleMap(mapEl)
      }
    } catch (error) {
      return
    }

    this.mapReady = true

    if (this.activeCountryKey) {
      this.showCountryOnMap(this.activeCountryKey)
    }
  }

  async initGoogleMap (mapEl) {
    const apiKey = this.dataset.mapsApiKey
    if (!apiKey) throw new Error('Missing Google Maps API key')

    await this.loadMapsApi(apiKey)
    this.buildGoogleMarkerIcon()

    this.map = new google.maps.Map(mapEl, {
      zoom: 4,
      center: { lat: 20, lng: 0 },
      disableDefaultUI: true,
      zoomControl: true,
      clickableIcons: false,
      styles: MAP_STYLES,
    })
  }

  async initLeafletMap (mapEl) {
    await this.loadLeaflet()
    this.buildLeafletMarkerIcon()

    this.map = L.map(mapEl, {
      zoomControl: true,
      attributionControl: true,
      scrollWheelZoom: false,
    }).setView([20, 0], 2)

    L.tileLayer(
      'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
      {
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
        subdomains: 'abcd',
        maxZoom: 20,
      },
    ).addTo(this.map)

    // Leaflet needs a size invalidate when mounting into a stretched column
    requestAnimationFrame(() => this.map.invalidateSize())
  }

  loadMapsApi (apiKey) {
    if (window.google?.maps) return Promise.resolve()
    if (window.__storeLocationsMapsPromise) {
      return window.__storeLocationsMapsPromise
    }

    window.__storeLocationsMapsPromise = new Promise((resolve, reject) => {
      const callbackName = '__storeLocationsMapsInit'
      window[callbackName] = () => resolve()

      const params = new URLSearchParams({
        key: apiKey,
        callback: callbackName,
        loading: 'async',
      })

      const script = document.createElement('script')
      script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`
      script.async = true
      script.onerror = () => reject(new Error('Google Maps failed to load'))

      document.head.appendChild(script)
    })

    return window.__storeLocationsMapsPromise
  }

  loadLeaflet () {
    if (window.L) return Promise.resolve()
    if (window.__storeLocationsLeafletPromise) {
      return window.__storeLocationsLeafletPromise
    }

    window.__storeLocationsLeafletPromise = new Promise((resolve, reject) => {
      if (!document.querySelector(`link[href="${LEAFLET_CSS}"]`)) {
        const link = document.createElement('link')
        link.rel = 'stylesheet'
        link.href = LEAFLET_CSS
        document.head.appendChild(link)
      }

      const script = document.createElement('script')
      script.src = LEAFLET_JS
      script.async = true
      script.onload = () => resolve()
      script.onerror = () => reject(new Error('Leaflet failed to load'))
      document.head.appendChild(script)
    })

    return window.__storeLocationsLeafletPromise
  }

  getMarkerBadgeMarkup () {
    if (this.markerBadgeHtml) return this.markerBadgeHtml

    this.markerBadgeHtml =
      '<svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 25 25"><circle cx="12.5" cy="12.5" r="12.5" fill="#000000"/></svg>'

    return this.markerBadgeHtml
  }

  buildGoogleMarkerIcon () {
    if (this.markerIcon) return

    this.markerIcon = {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
        this.getMarkerBadgeMarkup(),
      )}`,
      scaledSize: new google.maps.Size(25, 25),
      anchor: new google.maps.Point(12.5, 12.5),
    }
  }

  buildLeafletMarkerIcon () {
    if (this.markerIcon) return

    this.markerIcon = L.divIcon({
      className: 'sec-StoreLocations_MapMarker',
      html: this.getMarkerBadgeMarkup(),
      iconSize: [25, 25],
      iconAnchor: [12.5, 12.5],
    })
  }

  getStoresGroupEl (countryKey) {
    if (!this.els?.['stores-group']?.exists) return null

    return (
      this.els['stores-group'].elements.find(
        (groupEl) => groupEl.dataset.countryGroup === countryKey,
      ) || null
    )
  }

  showCountryOnMap (countryKey) {
    if (!this.mapReady) return

    this.clearMarkers()

    const groupEl = this.getStoresGroupEl(countryKey)
    if (!groupEl) return

    const itemEls = Array.from(
      groupEl.querySelectorAll(
        '[data-store-locations-el="store-item"][data-lat]',
      ),
    )

    if (this.devMode) {
      this.showCountryOnLeaflet(itemEls)
    } else {
      this.showCountryOnGoogle(itemEls)
    }

    this.fitActiveCountry()
  }

  showCountryOnGoogle (itemEls) {
    const bounds = new google.maps.LatLngBounds()

    itemEls.forEach((itemEl) => {
      const lat = Number.parseFloat(itemEl.dataset.lat)
      const lng = Number.parseFloat(itemEl.dataset.lng)

      if (Number.isNaN(lat) || Number.isNaN(lng)) return

      const position = { lat, lng }
      const marker = new google.maps.Marker({
        map: this.map,
        position,
        icon: this.markerIcon,
      })

      marker.addListener('click', () => this.onMarkerClick(itemEl))

      this.markers.push({ marker, storeItemEl: itemEl, position })
      bounds.extend(position)
    })

    this.currentBounds = this.markers.length ? bounds : null
  }

  showCountryOnLeaflet (itemEls) {
    const latLngs = []

    itemEls.forEach((itemEl) => {
      const lat = Number.parseFloat(itemEl.dataset.lat)
      const lng = Number.parseFloat(itemEl.dataset.lng)

      if (Number.isNaN(lat) || Number.isNaN(lng)) return

      const position = { lat, lng }
      const marker = L.marker([lat, lng], { icon: this.markerIcon }).addTo(
        this.map,
      )

      marker.on('click', () => this.onMarkerClick(itemEl))

      this.markers.push({ marker, storeItemEl: itemEl, position })
      latLngs.push([lat, lng])
    })

    this.currentBounds = latLngs.length ? L.latLngBounds(latLngs) : null
  }

  clearMarkers () {
    this.markers.forEach(({ marker }) => {
      if (this.devMode) {
        this.map.removeLayer(marker)
      } else {
        marker.setMap(null)
      }
    })
    this.markers = []
    this.currentBounds = null
  }

  fitActiveCountry () {
    if (!this.mapReady || this.markers.length === 0) return

    if (this.markers.length === 1) {
      const { position } = this.markers[0]

      if (this.devMode) {
        this.map.setView([position.lat, position.lng], COUNTRY_ZOOM)
      } else {
        this.map.setCenter(position)
        this.map.setZoom(COUNTRY_ZOOM)
      }
      return
    }

    if (!this.currentBounds) return

    if (this.devMode) {
      this.map.fitBounds(this.currentBounds, {
        padding: [BOUNDS_PADDING, BOUNDS_PADDING],
      })
    } else {
      this.map.fitBounds(this.currentBounds, BOUNDS_PADDING)
    }
  }

  focusStoreOnMap (storeItemEl) {
    if (!this.mapReady) return

    const entry = this.markers.find((item) => item.storeItemEl === storeItemEl)
    if (!entry) return

    if (this.devMode) {
      this.map.setView([entry.position.lat, entry.position.lng], STORE_ZOOM, {
        animate: true,
      })
    } else {
      this.map.panTo(entry.position)
      this.map.setZoom(STORE_ZOOM)
    }
  }

  onMarkerClick (storeItemEl) {
    if (storeItemEl === this.activeStoreItemEl) {
      this.focusStoreOnMap(storeItemEl)
      return
    }

    this.collapseActiveStore()
    this.expandStore(storeItemEl, { scrollIntoView: true })
  }
}

if (!customElements.get('store-locations')) {
  customElements.define('store-locations', StoreLocations)
}
